/**
 * Manager Scorecard v2 — coaching insights derived from existing scorecard
 * payloads. Pure functions; no fetches, no schema awareness, no LLM. Each
 * helper takes already-loaded data (scorecard rows from
 * screenScorecardData.views[viewMode].rows, plus attendance summary +
 * support counts already on the page) and emits compact display objects.
 */

export const PERSISTENT_DECLINE_PERIODS = 3;
export const ATTENDANCE_CLUSTER_THRESHOLD = 2;
export const STABLE_DELTA_THRESHOLD = 1; // |Δ| < this is "Stable"

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function safeFixed(value, digits = 1) {
  if (!isFiniteNumber(value)) return '—';
  return value.toFixed(digits);
}

/**
 * Compute period-over-period delta from a row's history array.
 *
 * `row.history` is the last-N (typically 12) raw actuals for the KPI,
 * sorted chronologically. We compare the last two values: last vs.
 * second-to-last is the per-period change.
 *
 * Returns:
 *   {
 *     hasComparison: boolean,
 *     direction: 'up' | 'down' | 'flat',
 *     // For lower-better KPIs (e.g. cycle time), `direction` reflects
 *     // PERFORMANCE direction, not raw value direction — going DOWN on
 *     // a lower-better metric is still 'up' (improving).
 *     deltaValue: number | null,    // raw absolute delta in KPI's unit
 *     deltaPercent: number | null,  // % change vs. prior period (for $/count KPIs)
 *     deltaPp: number | null,       // pp change (for % KPIs)
 *     label: string                 // user-facing summary string
 *   }
 */
export function computePeriodDelta(row = {}) {
  const history = Array.isArray(row.history) ? row.history.filter(isFiniteNumber) : [];
  if (history.length < 2) {
    return {
      hasComparison: false,
      direction: 'flat',
      deltaValue: null,
      deltaPercent: null,
      deltaPp: null,
      label: 'No prior period'
    };
  }

  const current = history[history.length - 1];
  const prior = history[history.length - 2];
  const deltaValue = current - prior;
  const isPercent = String(row.unit || '').trim() === '%';
  const isHigherBetter = row.isHigherBetter !== false; // default true unless explicitly flipped

  let deltaPp = null;
  let deltaPercent = null;
  if (isPercent) {
    deltaPp = deltaValue;
  } else if (prior !== 0) {
    deltaPercent = (deltaValue / prior) * 100;
  }

  // Direction reflects PERFORMANCE, not raw value:
  // higher-better KPI going up = 'up' (improving)
  // lower-better KPI going down = 'up' (improving)
  const referenceDelta = isPercent ? deltaPp : deltaPercent;
  const stableThreshold = STABLE_DELTA_THRESHOLD;
  let direction = 'flat';
  if (isFiniteNumber(referenceDelta)) {
    if (Math.abs(referenceDelta) < stableThreshold) {
      direction = 'flat';
    } else if (referenceDelta > 0) {
      direction = isHigherBetter ? 'up' : 'down';
    } else {
      direction = isHigherBetter ? 'down' : 'up';
    }
  }

  let label;
  if (direction === 'flat') {
    label = 'Stable';
  } else if (isPercent) {
    const sign = deltaPp > 0 ? '+' : '';
    label = `${sign}${safeFixed(deltaPp)} pp vs. last period`;
  } else if (isFiniteNumber(deltaPercent)) {
    const sign = deltaPercent > 0 ? '+' : '';
    label = `${sign}${safeFixed(deltaPercent)}% vs. last period`;
  } else {
    label = 'No comparison';
  }

  return {
    hasComparison: true,
    direction,
    deltaValue,
    deltaPercent,
    deltaPp,
    label
  };
}

/**
 * Pick the three drivers a director should know about:
 *   - biggestDrag: lowest achievement among warning rows
 *   - biggestGain: highest achievement among ahead-of-plan rows
 *   - mostImproved: largest positive period-over-period delta
 *
 * Any of the three can be null when there's no qualifying row.
 */
export function pickDrivers(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  const withAchievement = list.filter(r => isFiniteNumber(r.achievementPercent));

  const biggestDrag = withAchievement
    .filter(r => r.statusTone === 'warning')
    .sort((a, b) => a.achievementPercent - b.achievementPercent)[0] || null;

  const biggestGain = withAchievement
    .filter(r => r.statusTone === 'success' || (r.statusTone !== 'warning' && r.achievementPercent >= 100))
    .sort((a, b) => b.achievementPercent - a.achievementPercent)[0] || null;

  // Score each row by performance-direction delta magnitude.
  const scored = list.map(row => {
    const d = computePeriodDelta(row);
    if (!d.hasComparison) return { row, score: -Infinity };
    const ref = isFiniteNumber(d.deltaPp) ? d.deltaPp : d.deltaPercent;
    if (!isFiniteNumber(ref)) return { row, score: -Infinity };
    const isHigherBetter = row.isHigherBetter !== false;
    const performanceDelta = isHigherBetter ? ref : -ref;
    return { row, score: performanceDelta };
  }).filter(s => s.score > STABLE_DELTA_THRESHOLD);

  const mostImproved = scored.length
    ? scored.sort((a, b) => b.score - a.score)[0].row
    : null;

  return { biggestDrag, biggestGain, mostImproved };
}

/**
 * One-sentence headline for the manager scorecard. Names the biggest drag
 * by KPI, surfaces an attendance flag if relevant, omits gracefully when
 * data is missing.
 */
export function buildHeadlineNarrative({
  summary = null,
  drivers = null,
  attendanceSummary = null,
  managerName = ''
} = {}) {
  const score = isFiniteNumber(summary?.overallScore) ? Math.round(summary.overallScore) : null;
  const drag = drivers?.biggestDrag || null;
  const dragLabel = drag?.label || null;
  const unplanned = Number(attendanceSummary?.unplannedAbsenceCount
    || attendanceSummary?.unplannedAbsences || 0);

  const parts = [];
  if (score !== null) parts.push(`Score ${score}`);
  if (dragLabel) {
    parts.push(`${dragLabel} is the biggest drag`);
  }
  if (unplanned >= ATTENDANCE_CLUSTER_THRESHOLD) {
    const who = managerName ? managerName.split(/\s+/)[0] : 'They';
    parts.push(`${who} had ${unplanned} unplanned absences this period`);
  }

  if (!parts.length) {
    return managerName
      ? `Awaiting first reports for ${managerName} this period.`
      : 'Awaiting first reports this period.';
  }
  return `${parts.join(' — ')}.`;
}

/**
 * Surface up to 3 risk patterns derived from existing scorecard rows +
 * attendance summary + support counts. Each risk has a stable id, a
 * tone, a one-line title, and an optional subtitle.
 */
export function buildRiskPatterns({
  rows = [],
  attendanceSummary = null,
  supportCounts = null
} = {}) {
  const out = [];
  const list = Array.isArray(rows) ? rows : [];

  // Rule 1 — persistent decline. A KPI whose last N actuals all sit at
  // or below "warning territory" relative to the current target. We use
  // the current target as a constant approximation since per-period
  // targets aren't on the row payload.
  for (const row of list) {
    const history = Array.isArray(row.history) ? row.history.filter(isFiniteNumber) : [];
    const target = Number(row.target);
    if (history.length < PERSISTENT_DECLINE_PERIODS || !isFiniteNumber(target) || target === 0) {
      continue;
    }
    const recent = history.slice(-PERSISTENT_DECLINE_PERIODS);
    const isHigherBetter = row.isHigherBetter !== false;
    const isMiss = (value) => (isHigherBetter ? value < target * 0.95 : value > target * 1.05);
    const allMissing = recent.every(isMiss);
    if (allMissing) {
      const series = recent
        .map(v => isFiniteNumber(v) ? safeFixed(v, 0) + (String(row.unit || '') === '%' ? '%' : '') : '—')
        .join(', ');
      out.push({
        id: `risk-decline:${row.kpiKey}`,
        tone: 'behind',
        title: `Behind on ${row.label || row.kpiKey} for ${PERSISTENT_DECLINE_PERIODS} periods running`,
        subtitle: `Last ${PERSISTENT_DECLINE_PERIODS} periods: ${series}`,
        cta: { label: 'Open KPI', kind: 'kpi-drill', kpiKey: row.kpiKey }
      });
      if (out.length >= 3) break;
    }
  }

  // Rule 2 — unplanned absence cluster.
  const unplanned = Number(attendanceSummary?.unplannedAbsenceCount
    || attendanceSummary?.unplannedAbsences || 0);
  if (out.length < 3 && unplanned >= ATTENDANCE_CLUSTER_THRESHOLD) {
    out.push({
      id: 'risk-attendance',
      tone: 'watch',
      title: `${unplanned} unplanned absence${unplanned === 1 ? '' : 's'} in the last 30 days`,
      subtitle: 'Attendance pattern may be affecting performance signals.',
      cta: { label: 'Open attendance', kind: 'attendance', path: '/manager-performance' }
    });
  }

  // Rule 3 — workflow drag (returned goals + flagged actuals + perf-impact).
  if (out.length < 3 && supportCounts) {
    const byDomain = supportCounts.byDomain || {};
    const returned = Number(byDomain.goal || 0);
    const flagged = Number(byDomain.flagged_actual || 0);
    const impact = Number(byDomain.performance_impact || 0);
    const total = returned + flagged + impact;
    if (total > 0) {
      const segments = [];
      if (returned) segments.push(`${returned} goal proposal${returned === 1 ? '' : 's'} awaiting revision`);
      if (flagged) segments.push(`${flagged} flagged actual${flagged === 1 ? '' : 's'}`);
      if (impact) segments.push(`${impact} performance impact item${impact === 1 ? '' : 's'}`);
      out.push({
        id: 'risk-workflow',
        tone: 'watch',
        title: `${total} workflow item${total === 1 ? '' : 's'} need attention`,
        subtitle: segments.join(' · '),
        cta: { label: 'Open queue', kind: 'approvals', path: '/approvals' }
      });
    }
  }

  return out.slice(0, 3);
}

/**
 * Coaching topics — symmetric to risks, plus optional acknowledgement
 * of the biggest gain and a default fallback for healthy weeks.
 */
export function buildCoachingTopics({ risks = [], drivers = null } = {}) {
  const out = [];

  for (const risk of risks) {
    if (risk.id?.startsWith('risk-decline:')) {
      const kpiLabel = risk.title.replace(/^Behind on\s+/, '').replace(/\s+for\s+\d+\s+periods.*$/, '');
      out.push({
        id: `topic-${risk.id}`,
        tone: 'behind',
        title: `Discuss ${kpiLabel} trend`,
        subtitle: `${PERSISTENT_DECLINE_PERIODS}-period decline — surface root cause and a recovery plan.`,
        cta: risk.cta
      });
    } else if (risk.id === 'risk-attendance') {
      out.push({
        id: 'topic-attendance',
        tone: 'watch',
        title: 'Review attendance pattern together',
        subtitle: 'Confirm whether absences are personal, scheduling, or coverage-driven.',
        cta: risk.cta
      });
    } else if (risk.id === 'risk-workflow') {
      out.push({
        id: 'topic-workflow',
        tone: 'watch',
        title: 'Clear pending workflow items',
        subtitle: 'Returned goals and flagged actuals slow the cycle.',
        cta: risk.cta
      });
    }
    if (out.length >= 3) break;
  }

  // Acknowledgement when there's a clear gain to recognize.
  if (out.length < 3 && drivers?.biggestGain) {
    const g = drivers.biggestGain;
    out.push({
      id: `topic-acknowledge:${g.kpiKey}`,
      tone: 'good',
      title: `Acknowledge ${g.label}`,
      subtitle: `Tracking at ${safeFixed(g.achievementPercent, 0)}% of goal — worth a manager-review note.`,
      cta: { label: 'Open KPI', kind: 'kpi-drill', kpiKey: g.kpiKey }
    });
  }

  // Default fallback so the panel never reads empty for a healthy
  // tenant.
  if (out.length === 0) {
    out.push({
      id: 'topic-plan-next',
      tone: 'good',
      title: 'Set goals for the next period together',
      subtitle: 'Lock in next-period targets while everything is on track.',
      cta: { label: 'Open goals', kind: 'goals', path: '/goals' }
    });
  }

  return out.slice(0, 3);
}

